import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatShortDate } from '../../../utils/dateUtils';

const ProjectTable = ({ projects, selectedProjects, onSelectAll, onSelectProject, onEdit, onView, onStatusUpdate, onDelete }) => {

  const getStatusColor = (status) => {
    switch (status) {
      case 'ongoing':
        return 'bg-accent text-accent-foreground';
      case 'completed':
        return 'bg-success text-success-foreground';
      case 'planned':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ongoing':
        return 'Play';
      case 'completed':
        return 'CheckCircle';
      case 'planned':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-success';
    if (progress >= 50) return 'bg-accent';
    if (progress >= 25) return 'bg-warning';
    return 'bg-error';
  };

  const isAllSelected = projects?.length > 0 && selectedProjects?.length === projects?.length;
  const isIndeterminate = selectedProjects?.length > 0 && selectedProjects?.length < projects?.length;

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3 text-right">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isIndeterminate;
                  }}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                  className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                />
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">المشروع</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">الحالة</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">التقدم</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">الفئة</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">التاريخ</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">الأعضاء</th>
              <th className="w-32 px-4 py-3 text-right text-sm font-medium text-foreground">الإجراءات</th>
            </tr>
          </thead>
          
          <tbody className="divide-y divide-border">
            {projects?.map((project) => (
              <tr 
                key={project?.id} 
                className={`hover:bg-muted/30 transition-colors ${
                  selectedProjects?.includes(project?.id) ? 'bg-primary/5' : ''
                }`}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedProjects?.includes(project?.id)}
                    onChange={(e) => onSelectProject(project?.id, e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                  />
                </td>
                
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    {project?.image && (
                      <div className="flex-shrink-0">
                        <Image
                          src={project?.image}
                          alt={project?.imageAlt}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      </div>
                    )}
                    
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">
                        {project?.title}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {project?.description}
                      </p>
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project?.status)}`}>
                    <Icon name={getStatusIcon(project?.status)} size={12} className="mr-1" />
                    {project?.status === 'ongoing' ? 'جاري' : project?.status === 'completed' ? 'مكتمل' : 'مخطط'}
                  </span>
                </td>
                
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">{project?.progress}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(project?.progress)}`}
                          style={{ width: `${project?.progress}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">
                    {project?.category}
                  </span>
                </td>
                
                <td className="px-4 py-4">
                  <div className="text-xs">
                    <p className="text-foreground font-medium">
                      {formatShortDate(project?.startDate)}
                    </p>
                    <p className="text-muted-foreground">
                      إلى {formatShortDate(project?.endDate)}
                    </p>
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">
                      {project?.memberCount}
                    </span>
                  </div>
                </td>
                
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(project)}
                      iconName="Eye"
                      iconSize={14}
                      title="عرض التفاصيل"
                      className="w-8 h-8"
                    />
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(project)}
                      iconName="Edit"
                      iconSize={14}
                      title="تحرير المشروع"
                      className="w-8 h-8"
                    />
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onStatusUpdate(project)}
                      iconName="RefreshCw"
                      iconSize={14}
                      title="تحديث الحالة"
                      className="w-8 h-8"
                    />
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(project)}
                      iconName="Trash2"
                      iconSize={14}
                      title="حذف المشروع"
                      className="w-8 h-8 text-error hover:text-error hover:bg-error/10"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {projects?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FolderOpen" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">لا توجد مشاريع</h3>
          <p className="text-muted-foreground">لم يتم العثور على مشاريع تطابق معايير البحث</p>
        </div>
      )}
    </div>
  );
};

export default ProjectTable;